import * as React from "react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth } from "../../store/auth_slice";
import {
  fetchUserDrones,
  selectAllDrones,
  selectDronesLoading,
  selectDronesError,
  DroneDTO
} from "../../store/drone_slice";
import { AppDispatch } from "../../store/store";
import ProfileCard from "../../common/profile_card/profile_card";
// import CustomCard from "../../common/custom_card/custom_card";
import Header from "../../common/header/header";
import Footer from "../../common/footer/footer";
import SkeletonLoader from "../../common/skeleton/skeleton_loader";

const MyListings: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(selectAuth);
  const loading = useSelector(selectDronesLoading);
  const error = useSelector(selectDronesError);
  const initialLoadDone = useRef(false);

  // Get user's drones using the selector
  const userDrones = useSelector(selectAllDrones);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Only fetch drones once when the component mounts and user is available
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      dispatch(fetchUserDrones());
    }
  }, [dispatch, user, navigate]);

  if (!user) {
    return null; // or a loading state if you prefer
  }

  if (loading) {
    return <SkeletonLoader type="listings" />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-grow w-full flex justify-center">
          <div className="w-full max-w-7xl p-4">
            <Header />
            <div className="py-8 text-red-500">Error loading your listings: {error}</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow w-full flex justify-center">
        <div className="w-full max-w-7xl p-4">
          <Header />
          <div className="flex flex-col md:flex-row gap-6 py-20">
            <div className="md:w-1/3 space-y-4">
              <ProfileCard
                name={`${user.firstName} ${user.lastName}`}
                memberSince={user.creationYear}
                location="Romania"
              />
              {/* <CustomCard
                imageSrc="/path-to-message-icon.png"
                imageAlt="Message"
                buttonText="Go to My Messages"
                backgroundColor="bg-bluePrimary"
                onClick={() => navigate('/messages')}
              /> */}
            </div>
            <div className="md:w-2/3">
              <div className="flex justify-between items-center mb-6">
                <h1 className="font-ubuntu text-3xl font-bold">My Listings</h1>
                <button
                  onClick={() => navigate('/drone-service')}
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-opacity-90"
                >
                  Add New Listing
                </button>
              </div>

              {userDrones.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">You haven't posted any drone listings yet.</p>
                  <button
                    onClick={() => navigate('/drone-service')}
                    className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-opacity-90"
                  >
                    Post Your First Drone
                  </button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {userDrones.map((drone: DroneDTO) => (
                    <div
                      key={drone.id}
                      className="border rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => navigate(`/product/${drone.id}`)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h2 className="text-xl font-semibold">{drone.name}</h2>
                          <p className="text-gray-600">{drone.location}</p>
                          <p className="text-primary font-semibold">{drone.currency} {drone.price}/hour</p>
                        </div>
                        {drone.imageUrls?.[0] && (
                          <img
                            src={drone.imageUrls[0]}
                            alt={drone.name}
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MyListings;
